import { STORAGE_KEY, TOTAL_BONES } from './constants';
import { emit } from './events';
import type { SceneId } from '../scenes/scene.types';
import { readJson, removeKey, writeJson } from '../utils/storage';

export interface VirtualShopStateV1 {
  schemaVersion: 1;
  visitedScenes: SceneId[];
  discoveredInteractions: string[];
  collectedBones: string[];
  questCompleted: boolean;
  easterEggs: string[];
  seenDialogues: string[];
  updatedAt: string;
}

function emptyState(): VirtualShopStateV1 {
  return {
    schemaVersion: 1,
    visitedScenes: [],
    discoveredInteractions: [],
    collectedBones: [],
    questCompleted: false,
    easterEggs: [],
    seenDialogues: [],
    updatedAt: new Date().toISOString(),
  };
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

/** Ne fait confiance à rien de ce qui vient du stockage : tout champ douteux est reconstruit. */
function sanitize(raw: unknown): VirtualShopStateV1 {
  const base = emptyState();
  if (typeof raw !== 'object' || raw === null) return base;
  const candidate = raw as Partial<VirtualShopStateV1>;
  if (candidate.schemaVersion !== 1) return base;
  return {
    schemaVersion: 1,
    visitedScenes: isStringArray(candidate.visitedScenes)
      ? (candidate.visitedScenes.filter((id): id is SceneId =>
          ['hall', 'alimentation', 'friandises', 'comptoir'].includes(id),
        ) as SceneId[])
      : [],
    discoveredInteractions: isStringArray(candidate.discoveredInteractions)
      ? candidate.discoveredInteractions
      : [],
    collectedBones: isStringArray(candidate.collectedBones) ? candidate.collectedBones : [],
    questCompleted: candidate.questCompleted === true,
    easterEggs: isStringArray(candidate.easterEggs) ? candidate.easterEggs : [],
    seenDialogues: isStringArray(candidate.seenDialogues) ? candidate.seenDialogues : [],
    updatedAt: typeof candidate.updatedAt === 'string' ? candidate.updatedAt : base.updatedAt,
  };
}

let state: VirtualShopStateV1 = emptyState();

export function loadState(): VirtualShopStateV1 {
  state = sanitize(readJson<unknown>(STORAGE_KEY));
  return getState();
}

export function getState(): Readonly<VirtualShopStateV1> {
  return state;
}

function persist(): void {
  state.updatedAt = new Date().toISOString();
  writeJson(STORAGE_KEY, state);
  emit('state:changed', {});
}

export function markSceneVisited(sceneId: SceneId): boolean {
  if (state.visitedScenes.includes(sceneId)) return false;
  state.visitedScenes.push(sceneId);
  persist();
  return true;
}

export function hasVisited(sceneId: SceneId): boolean {
  return state.visitedScenes.includes(sceneId);
}

export function markInteractionDiscovered(interactionId: string): boolean {
  if (state.discoveredInteractions.includes(interactionId)) return false;
  state.discoveredInteractions.push(interactionId);
  persist();
  return true;
}

export function hasDiscovered(interactionId: string): boolean {
  return state.discoveredInteractions.includes(interactionId);
}

export function markDialogueSeen(dialogueId: string): void {
  if (state.seenDialogues.includes(dialogueId)) return;
  state.seenDialogues.push(dialogueId);
  persist();
}

export function hasSeenDialogue(dialogueId: string): boolean {
  return state.seenDialogues.includes(dialogueId);
}

/** Renvoie false si l'os avait déjà été ramassé : pas de double comptage. */
export function collectBone(boneId: string): boolean {
  if (state.collectedBones.includes(boneId)) return false;
  state.collectedBones.push(boneId);
  persist();
  emit('bone:collected', { boneId, total: state.collectedBones.length });
  return true;
}

export function bonesFound(): number {
  return state.collectedBones.length;
}

export function allBonesFound(): boolean {
  return state.collectedBones.length >= TOTAL_BONES;
}

export function completeQuest(): boolean {
  if (state.questCompleted || !allBonesFound()) return false;
  state.questCompleted = true;
  persist();
  emit('quest:completed', {});
  return true;
}

export function findEasterEgg(eggId: string): boolean {
  if (state.easterEggs.includes(eggId)) return false;
  state.easterEggs.push(eggId);
  persist();
  emit('easter-egg:found', { eggId });
  return true;
}

/** « Recommencer la visite » : efface tout, y compris le stockage. */
export function resetState(): void {
  state = emptyState();
  removeKey(STORAGE_KEY);
  emit('state:changed', {});
}
